import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';
import Sources from '../../components/Sources';
import FAQSection from '../../components/FAQSection';
import TableOfContents from '../../components/TableOfContents';
import Callout from '../../components/Callout';
import Breadcrumbs from '../../components/Breadcrumbs';
import { LoadDimensionsDiagram } from '../../components/Diagrams';

export default function TrailerLaengdeOgBredde() {
  const breadcrumbs = [
    { name: "Forside", path: "/" },
    { name: "Kørekort og vægt", path: "/trailer-regler" },
    { name: "Længde, bredde og last", path: "/trailer-laengde-og-bredde" }
  ];

  const tocItems = [
    { id: "maksimale-dimensioner", title: "Maksimale mål for trailer og vogntog" },
    { id: "gods-der-stikker-ud-bagud", title: "Gods der stikker ud bagud (1-meters-reglen)" },
    { id: "gods-der-stikker-ud-til-siden", title: "Gods der rager ud til siderne (15 cm-reglen)" },
    { id: "afmaerkning-dag-og-nat", title: "Krav til korrekt afmærkning dag og nat" },
    { id: "synlighed-af-lygter-og-skilt", title: "Forbud mod tildækning af lygter og plade" },
    { id: "saertransport-dispensation", title: "Hvornår kræves særtransporttilladelse?" }
  ];

  const faq = [
    {
      q: "Hvor langt må gods stikke ud bag på traileren uden afmærkning?",
      a: "Gods må højst rage 1,00 meter ud over trailerens bagerste punkt (herunder bagerste kofanger eller lygtebom) uden afmærkning. Rager godset mere end 1 meter ud, skal det altid afmærkes efter færdselslovens § 82."
    },
    {
      q: "Hvordan skal fremspringende gods afmærkes om dagen?",
      a: "Om dagen i klart vejr afmærkes fremspringende gods med en lys klud (mindst 30 x 30 cm) eller en godkendt rød/hvid afmærkningscylinder, der er solidt fastgjort i den absolut yderste ende af godset."
    },
    {
      q: "Hvordan skal fremspringende gods afmærkes i mørke?",
      a: "I lygtetændingstiden (fra solnedgang til solopgang samt i usigtbart vejr) skal fremspringende gods bagud afmærkes med mindst én lygte, der lyser rødt bagud, samt mindst én rød refleks. Rager godset fremad, kræves hvid lygte og hvid refleks."
    },
    {
      q: "Hvor bred må en trailer maksimalt være i Danmark?",
      a: "I Danmark og resten af EU må en almindelig trailer maksimalt have en bredde på 2,55 meter inklusive hjulkasser, hængsler og faste beslag, men eksklusive fleksible positionslygter og sidespejle."
    },
    {
      q: "Hvor meget må gods rage ud over trailerens sider?",
      a: "Gods må maksimalt stikke 15 cm ud over trailerens sidefjæle i hver side, og den samlede bredde (trailer plus last) må under ingen omstændigheder overstige 2,55 meter på offentlig vej."
    }
  ];

  const sources = [
    { title: "Dimensionsbekendtgørelsen (BEK nr. 577 af 30/03/2021)", source: "retsinformation.dk", url: "https://www.retsinformation.dk/eli/lta/2021/577" },
    { title: "Færdselsloven (LBK nr. 1324 af 18/09/2023) § 82", source: "retsinformation.dk", url: "https://www.retsinformation.dk/eli/lta/2023/1324" }
  ];

  return (
    <>
      <SEO 
        title="Regler for trailerens længde, bredde og last" 
        description="Maksimal bredde og længde for trailer og vogntog i Danmark. Lær reglerne for gods der stikker ud bagud og til siderne, samt lovpligtig afmærkning." 
        pathname="/trailer-laengde-og-bredde"
        faq={faq}
        breadcrumbs={breadcrumbs}
      />

      <article>
        <Breadcrumbs items={breadcrumbs} />

        <h1 className="text-[30px] sm:text-[36px] font-bold text-[#2E3A46] tracking-tight leading-tight mb-4">
          Trailerens længde, bredde og fremspringende last
        </h1>

        <p className="text-[18px] text-[#1A1C1F] leading-relaxed mb-6 font-medium">
          En trailer må i Danmark maksimalt være <strong>2,55 meter bred, 4,00 meter høj og 12,00 meter lang</strong> inklusive trækstang. Det samlede vogntog bestående af bil og trailer må maksimalt have en samlet længde på <strong>18,75 meter</strong>.
        </p>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-6">
          Gods må højst rage 1,00 meter ud over trailerens bagerste punkt uden afmærkning. Stikker det mere end 1 meter ud, skal det afmærkes med en lys klud om dagen og med rød lygte og rød refleks i lygtetændingstiden. Til siderne må lasten maksimalt rage 15 cm ud over trailerens sider, og den samlede bredde må aldrig overskride 2,55 meter.
        </p>

        <TableOfContents items={tocItems} />

        <Callout title="Kort overblik over dimensionsreglerne">
          <ul className="m-0 p-0 list-none space-y-1 font-mono text-[15px]">
            <li>• Maksimal bredde for trailer og last: <strong>2,55 meter</strong></li>
            <li>• Maksimal højde for trailer og last: <strong>4,00 meter</strong></li>
            <li>• Maksimal samlet vogntogslængde: <strong>18,75 meter</strong></li>
            <li>• Fremspring bagud uden afmærkning: <strong>Højst 1,00 meter</strong></li>
            <li>• Fremspring til siderne: <strong>Maks. 15 cm pr. side (maks. 2,55 m i alt)</strong></li>
          </ul>
        </Callout>

        <h2 id="maksimale-dimensioner" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          Maksimale mål for trailer og vogntog
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          I henhold til den danske dimensionsbekendtgørelse (bekendtgørelse om køretøjers største bredde, længde, højde og vægt) er der fastsat klare grænser for påhængskøretøjer på det offentlige vejnet:
        </p>

        <div className="my-6 overflow-x-auto border-t border-[#B58200]">
          <table className="w-full text-left text-[15px] border-collapse">
            <thead>
              <tr className="border-b border-[#E4E1D8] font-mono text-[13px] uppercase text-[#2E3A46]">
                <th className="py-2.5 pr-4 font-semibold">Dimension</th>
                <th className="py-2.5 pr-4 font-semibold">Maksimal grænse</th>
                <th className="py-2.5 font-semibold">Lovgrundlag og bemærkning</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E4E1D8] tabular-nums">
              <tr>
                <td className="py-3 pr-4 font-bold text-[#2E3A46]">Maksimal bredde (trailer)</td>
                <td className="py-3 pr-4 font-semibold text-[#2E3A46]">2,55 meter</td>
                <td className="py-3 text-[#5C636B]">Måles over skærmkasser og faste dele</td>
              </tr>
              <tr>
                <td className="py-3 pr-4 font-bold text-[#2E3A46]">Maksimal bredde (kølekasse)</td>
                <td className="py-3 pr-4">2,60 meter</td>
                <td className="py-3 text-[#5C636B]">Gælder isolerede påbygninger</td>
              </tr>
              <tr>
                <td className="py-3 pr-4 font-bold text-[#2E3A46]">Maksimal højde</td>
                <td className="py-3 pr-4 font-semibold text-[#2E3A46]">4,00 meter</td>
                <td className="py-3 text-[#5C636B]">Inklusive det transporterede gods</td>
              </tr>
              <tr>
                <td className="py-3 pr-4 font-bold text-[#2E3A46]">Maksimal længde (trailer)</td>
                <td className="py-3 pr-4 font-semibold text-[#2E3A46]">12,00 meter</td>
                <td className="py-3 text-[#5C636B]">Målt fra forreste trækøje til bagerste punkt</td>
              </tr>
              <tr>
                <td className="py-3 pr-4 font-bold text-[#2E3A46]">Maksimal vogntogslængde</td>
                <td className="py-3 pr-4 font-bold text-[#2E3A46]">18,75 meter</td>
                <td className="py-3 text-[#5C636B]">Samlet længde af bil plus trailer</td>
              </tr>
            </tbody>
          </table>
        </div>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-6">
          De fleste almindelige hobbytrailere har en bredde på mellem 1,40 og 1,80 meter og en totallængde på 3,00 til 4,50 meter, og de ligger dermed langt under lovens maksimale grænseværdier. Reglerne bliver dog særligt relevante, når du transporterer langt gods såsom brædder, tagrender, rør eller både.
        </p>

        <h2 id="gods-der-stikker-ud-bagud" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          Gods der stikker ud bagud (1-meters-reglen)
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          Når du transporterer langt gods, må det gerne rage ud over trailerens bagklap, så længe vægtfordelingen er forsvarlig og kugletrykket overholdes (se <Link to="/kugletryk" className="text-[#2E3A46] font-medium hover:text-[#B58200] hover:underline underline-offset-2">kugletryk</Link>).
        </p>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          Færdselslovens § 82 opstiller en klar skillelinje ved præcis 1,00 meter:
        </p>

        <ul className="list-disc list-inside space-y-2 mb-6 text-[#1A1C1F]">
          <li><strong>Under 1,00 meter:</strong> Hvis godset rager højst 1,00 meter ud over trailerens bagerste punkt, kræves der ingen særlig afmærkning i dagslys.</li>
          <li><strong>Over 1,00 meter:</strong> Stikker godset mere end 1,00 meter ud, skal det altid forsynes med lovpligtig afmærkning – både i dagslys og i mørke.</li>
        </ul>

        <LoadDimensionsDiagram caption="Figur 1: Princippet for fremspringende last og kravet om afmærkning ved mere end 1 meters udhæng bagud." />

        <h2 id="gods-der-stikker-ud-til-siden" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          Gods der rager ud til siderne (15 cm-reglen)
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          Transporterer du brede plader, havemøbler eller haveaffald, gælder der to samtidige krav til godsets bredde:
        </p>

        <ol className="list-decimal list-inside space-y-2 mb-6 text-[#1A1C1F]">
          <li>Godset må højst rage <strong>15 cm ud over trailerens side</strong> (målt fra kanten af trailerens yderste del, typisk sidefjælen eller skærmkassen).</li>
          <li>Den samlede bredde på vogntoget inklusive godset må <strong>aldrig overstige 2,55 meter</strong>.</li>
        </ol>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-6">
          Hvis traileren f.eks. er 1,50 meter bred, må godset stikke 15 cm ud i hver side, hvilket giver en total bredde på 1,80 meter. Er traileren derimod 2,45 meter bred, må godset kun rage 5 cm ud i hver side for ikke at sprænge den absolutte grænse på 2,55 meter.
        </p>

        <h2 id="afmaerkning-dag-og-nat" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          Krav til korrekt afmærkning dag og nat
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          Hvis lasten rager mere end 1 meter ud bagud eller mere end 15 cm ud til siderne, skal den afmærkes efter følgende regler:
        </p>

        <div className="my-6 overflow-x-auto border-t border-[#B58200]">
          <table className="w-full text-left text-[15px] border-collapse">
            <thead>
              <tr className="border-b border-[#E4E1D8] font-mono text-[13px] uppercase text-[#2E3A46]">
                <th className="py-2.5 pr-4 font-semibold">Tidspunkt</th>
                <th className="py-2.5 pr-4 font-semibold">Afmærkningskrav bagud (&gt; 1 m)</th>
                <th className="py-2.5 font-semibold">Afmærkningskrav fremad eller til siden</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E4E1D8] tabular-nums">
              <tr>
                <td className="py-3 pr-4 font-bold text-[#2E3A46]">Dagslys (klart vejr)</td>
                <td className="py-3 pr-4">En lys klud (mindst 30 x 30 cm) eller cylinder</td>
                <td className="py-3 text-[#5C636B]">Lys klud eller afmærkningscylinder</td>
              </tr>
              <tr>
                <td className="py-3 pr-4 font-bold text-[#2E3A46]">Lygtetændingstid (mørke/tåge)</td>
                <td className="py-3 pr-4 font-bold text-[#B58200]">Mindst én rød lygte + rød refleks</td>
                <td className="py-3 text-[#5C636B]">Hvid lygte + hvid refleks fremad, rød lygte bagud</td>
              </tr>
            </tbody>
          </table>
        </div>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-6">
          Afmærkningen skal fastgøres forsvarligt i godsets absolutte yderpunkt, så den ikke kan blæse af under kørsel på motorvej eller landevej. En rød plasticpose eller en lille stump snor opfylder ikke lovens krav om en tydelig lys klud på mindst 30 x 30 cm.
        </p>

        <h2 id="synlighed-af-lygter-og-skilt" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          Forbud mod tildækning af lygter og plade
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          Uanset godsets dimensioner slår færdselslovens § 82 fast, at lasten aldrig må skjule trailerens påbudte lygter, reflekser eller nummerplade:
        </p>

        <ul className="list-disc list-inside space-y-2 mb-6 text-[#1A1C1F]">
          <li>Hænger godset ned over bagenden, så nummerpladen eller baglygterne ikke er fuldt synlige i de lovbefalede vinkler, skal der monteres en ekstra lygtebom bag på godset.</li>
          <li>Førerens udsyn i bakspejle og sidespejle må ikke hindres af lasten. Hvis godset rager bredere ud end trækbilen, skal der monteres ekstra sidespejle (forlængede campingspejle).</li>
        </ul>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-6">
          Læs mere om de lovpligtige lygter og krav til synlighed under <Link to="/trailer-lys-regler" className="text-[#2E3A46] font-medium hover:text-[#B58200] hover:underline underline-offset-2">trailer lys regler</Link>.
        </p>

        <h2 id="saertransport-dispensation" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          Hvornår kræves særtransporttilladelse?
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          Hvis du har behov for at transportere udeleligt gods, der overskrider 2,55 meter i bredden, 4,00 meter i højden eller 18,75 meter i vogntogslængde (f.eks. transport af en stor sejlbåd, en skurvogn eller en færdig samlet bygningsdel), må det ikke ske på almindelige vilkår.
        </p>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-6">
          I sådanne tilfælde skal der søges om en officiel særtransporttilladelse hos Vejdirektoratet og Politiet. Tilladelsen angiver faste kørselsruter, tidsrum (ofte uden for myldretid) og eventuelle krav om ledsagebiler med gule advarselsblink. For almindelig privat kørsel gælder det om at holde sig stramt inden for de standardiserede mål. Læs også om fastspænding under <Link to="/lastsikring" className="text-[#2E3A46] font-medium hover:text-[#B58200] hover:underline underline-offset-2">lastsikring</Link>.
        </p>

        <FAQSection items={faq} />
        <Sources links={sources} />
      </article>
    </>
  );
}
